/**
 * TurnstileWidget — thin wrapper around Cloudflare Turnstile that works
 * on any public checkout page.
 *
 * Usage:
 *   const [token, setToken] = useState('');
 *   <TurnstileWidget onToken={setToken} />
 *   // then include `turnstile_token: token` in the POST body
 *
 * Behavior:
 *   • Pulls the site key from GET /api/public/anti-abuse/config so the
 *     backend can rotate it without redeploying the FE.
 *   • Lazy-loads the Cloudflare script tag once.
 *   • Re-renders if the component unmounts/remounts.
 *   • Theme='dark' to match EntrepreNatives aesthetic.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;
function loadTurnstileScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) { resolve(); return; }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Failed to load Cloudflare Turnstile'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export default function TurnstileWidget({ onToken, action = 'checkout', className = '' }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [siteKey, setSiteKey] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/anti-abuse/config`);
        if (!cancelled) setSiteKey(data?.turnstile_site_key || '');
      } catch {
        if (!cancelled) setErr('Could not load security check');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;
    let destroyed = false;
    loadTurnstileScript()
      .then(() => {
        if (destroyed || !window.turnstile || !containerRef.current) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme: 'dark',
          size: 'flexible',
          action,
          callback: (token) => { onToken?.(token); },
          'error-callback': () => { onToken?.(''); setErr('Security check failed — refresh and try again.'); },
          'expired-callback': () => { onToken?.(''); },
        });
      })
      .catch((e) => setErr(e.message || 'Could not load security check'));
    return () => {
      destroyed = true;
      try {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch {}
    };
  // onToken intentionally omitted so a parent re-render doesn't re-mount
  // the widget (which would waste a Turnstile challenge).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, action]);

  return (
    <div className={className} data-testid="turnstile-widget">
      <div ref={containerRef} />
      {err && <p className="mt-2 text-[11px] text-red-300" data-testid="turnstile-error">{err}</p>}
    </div>
  );
}
